import { useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import HaloRing from "@/components/HaloRing";
import HaloWordmark from "@/components/HaloWordmark";
import { MailCheck, ArrowLeft } from "lucide-react";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/auth/forgot-password", { email });
      setSent(data.message);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setSent(typeof detail === "string" ? detail : "Something went wrong. Please try again shortly.");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen grid place-items-center bg-background px-6" data-testid="forgot-password-page">
      <div className="w-full max-w-md">
        <div className="mb-8 flex items-center gap-3">
          <HaloRing size={30} />
          <HaloWordmark height={19} data-testid="login-wordmark" />
        </div>

        {sent ? (
          <div className="studio-panel p-6" data-testid="forgot-sent-panel">
            <MailCheck className="h-6 w-6 text-[hsl(var(--halo-teal))]" />
            <h1 className="mt-4 text-2xl font-semibold tracking-tight">Check your inbox</h1>
            <p className="mt-3 text-sm leading-relaxed text-muted-foreground" data-testid="forgot-sent-message">
              {sent}
            </p>
            <p className="mt-3 text-xs leading-relaxed text-muted-foreground">
              Nothing arriving? Check spam, and make sure an email provider is set up under
              Settings → Email — reset emails go out through it.
            </p>
            <Link to="/login" className="mt-6 inline-block" data-testid="forgot-back-to-login">
              <Button variant="outline" className="rounded-full">
                <ArrowLeft className="mr-2 h-4 w-4" /> Back to sign in
              </Button>
            </Link>
          </div>
        ) : (
          <>
            <h1 className="text-3xl font-semibold tracking-tight">Forgot your password?</h1>
            <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
              Pop in your email and we'll send you a link to set a new one. It expires in an hour.
            </p>
            <form onSubmit={submit} className="mt-7 space-y-4" data-testid="forgot-form">
              <div>
                <Label htmlFor="fp-email">Email</Label>
                <Input id="fp-email" data-testid="forgot-email" type="email" required
                       value={email} onChange={(e) => setEmail(e.target.value)} />
              </div>
              <Button type="submit" disabled={busy} data-testid="forgot-submit"
                      className="h-11 w-full bg-[hsl(var(--studio-primary))] hover:bg-[hsl(var(--studio-primary-hover))]">
                {busy ? "Sending…" : "Send reset link"}
              </Button>
            </form>
            <Link to="/login" className="mt-6 inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground"
                  data-testid="forgot-cancel">
              <ArrowLeft className="h-3.5 w-3.5" /> Back to sign in
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
